/**
 * Atelier — Réservation (Le Zèbre à Pois)
 *
 * Parcours en 3 étapes (créneau → composants → coordonnées) posé sur le
 * formulaire de contact natif de Shopify. L'envoi recharge la page : la
 * configuration est donc mise de côté dans sessionStorage juste avant le POST,
 * puis relue pour reconstruire le récapitulatif imprimable.
 *
 * Sans JS, toutes les étapes restent visibles et le formulaire part quand même :
 * l'email contiendra les coordonnées, à défaut du détail des composants.
 *
 * La place n'est acquise qu'après un virement au Zèbre à Pois, à faire dans le
 * délai fixé dans l'éditeur (48 h par défaut). L'heure d'envoi est donc gardée
 * avec la configuration : c'est elle qui donne l'échéance affichée, le compte à
 * rebours, et la date envoyée dans le mail de réservation.
 */
(() => {
  const STORAGE_KEY = 'atelier-zebre:reservation';
  const LAST_STEP = 3;

  const euro = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const echeance = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });

  const cloture = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris',
  });

  const HEURE = 60 * 60 * 1000;

  /* ---------------------------------------------------------------- stockage */

  const store = {
    save(payload) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (error) {
        // Navigation privée ou quota plein : le récap sera dégradé, pas l'envoi.
      }
    },
    read() {
      try {
        return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      } catch (error) {
        return null;
      }
    },
  };

  /* ------------------------------------------------------------- utilitaires */

  function makeReference() {
    const now = new Date();
    const day = `${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
    let suffix = '';
    for (let i = 0; i < 4; i += 1) {
      suffix += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    return `ZAP-${day}-${suffix}`;
  }

  // Les clés de modèle sont saisies à la main dans l'éditeur : on normalise les
  // deux côtés pour que « SharpJust », « sharpjust » et « Sharp Just » se valent.
  function slug(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // L'échéance se déduit de l'heure d'envoi : rien n'est figé côté serveur, le
  // délai reste réglable dans l'éditeur sans toucher au code.
  function deadlineFrom(submittedAt, hours) {
    const start = Number(submittedAt);
    const span = Number(hours);
    if (!start || !span) return null;
    return new Date(start + span * HEURE);
  }

  // « 47 h 12 » plutôt qu'un décompte à la seconde : on ne regarde pas cette
  // page pendant deux jours, et la minute suffit pour comprendre l'urgence.
  function remainingText(deadline, now) {
    const left = deadline.getTime() - now;
    if (left <= 0) return null;

    const totalMinutes = Math.floor(left / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours >= 1) return `${hours} h ${String(minutes).padStart(2, '0')}`;
    return `${minutes} min`;
  }

  function parseKeys(value) {
    return String(value || '')
      .split(',')
      .map(slug)
      .filter(Boolean);
  }

  // « Argent » existe en boîtier, en aiguilles et en bracelet : une clé nue ne suffit
  // pas. Chaque option répond donc à deux clés, la nue et celle préfixée par sa
  // catégorie — on peut ainsi viser large à la main, ou précis à l'import.
  function optionKeys(input) {
    const category = input.closest('[data-category]');
    const title = slug(input.dataset.title);
    const keys = new Set([title]);

    // le nom préfixé par la catégorie, pour départager les homonymes
    if (category) keys.add(`${slug(category.dataset.label)}-${title}`);
    // et la clé explicite, quand elle est posée à la main ou par l'import
    if (input.dataset.modelKey) keys.add(slug(input.dataset.modelKey));

    keys.delete('');
    return Array.from(keys);
  }

  function collect(root) {
    const basePrice = Number(root.dataset.basePrice || 0) || 0;
    const slotInput = root.querySelector('[data-slot]:checked');
    const items = [];

    root.querySelectorAll('[data-category]').forEach((category) => {
      if (category.hidden) return;

      const chosen = category.querySelector('[data-option]:checked');
      if (!chosen) return;

      items.push({
        category: category.dataset.label || '',
        title: chosen.dataset.title || '',
        ref: chosen.dataset.ref || '',
        price: Number(chosen.dataset.price || 0) || 0,
      });
    });

    const total = items.reduce((sum, item) => sum + item.price, basePrice);

    return {
      basePrice,
      slot: slotInput
        ? { label: slotInput.dataset.slotLabel || '', hours: slotInput.dataset.slotHours || '' }
        : null,
      items,
      total,
    };
  }

  function slotText(slot) {
    if (!slot) return '';
    return slot.hours ? `${slot.label} (${slot.hours})` : slot.label;
  }

  function componentsText(items) {
    return items
      .map((item) => {
        const ref = item.ref ? ` — réf. ${item.ref}` : '';
        const extra = item.price > 0 ? ` (+ ${euro.format(item.price)})` : '';
        return `${item.category} : ${item.title}${ref}${extra}`;
      })
      .join('\n');
  }

  function bodyText(data, fields) {
    const lines = [
      `Réservation ${fields.reference}`,
      fields.eventLabel ? fields.eventLabel : null,
      '',
      `Créneau : ${slotText(data.slot) || 'non précisé'}`,
      `Participant : ${fields.name || '—'}`,
      `Téléphone : ${fields.phone || '—'}`,
      '',
      'Composants choisis :',
      componentsText(data.items) || '—',
      '',
      `Atelier : ${euro.format(data.basePrice)}`,
      `Montant du virement : ${euro.format(data.total)}`,
      fields.deadline ? `Virement attendu avant le ${fields.deadline}` : null,
    ];

    if (fields.note) {
      lines.push('', 'Note du participant :', fields.note);
    }

    return lines.filter((line) => line !== null).join('\n');
  }

  // « 6 j 04 h » loin de l'échéance, « 3 h 12 » le dernier jour : on donne la
  // précision utile au moment où elle l'est.
  function closingText(left) {
    const minutes = Math.floor(left / 60000);
    const heures = Math.floor(minutes / 60);
    const jours = Math.floor(heures / 24);

    if (jours >= 1) return `${jours} j ${String(heures % 24).padStart(2, '0')} h`;
    if (heures >= 1) return `${heures} h ${String(minutes % 60).padStart(2, '0')}`;
    return `${minutes} min`;
  }

  /* --- clôture des inscriptions --- */

  function renderClosing(root) {
    const zone = root.querySelector('[data-registration-countdown]');
    if (!zone) return;

    const closesAt = Number(root.dataset.closesAt || 0) * 1000;
    if (!closesAt) {
      zone.hidden = true;
      return;
    }

    const value = zone.querySelector('[data-countdown-value]');
    const dateNode = zone.querySelector('[data-countdown-date]');
    if (dateNode) dateNode.textContent = `— jusqu'au ${cloture.format(new Date(closesAt))}`;

    const tick = () => {
      const left = closesAt - Date.now();

      if (left <= 0) {
        // La page a pu être servie depuis le cache avant la clôture : on ferme
        // le formulaire ici plutôt que de laisser passer une inscription de trop.
        root.querySelectorAll('[data-step], .atelier-zebre__stepper').forEach((node) => {
          node.hidden = true;
        });
        zone.classList.add('is-urgent');
        if (value) value.textContent = 'Inscriptions closes';
        if (dateNode) dateNode.textContent = '';
        return false;
      }

      if (value) value.textContent = closingText(left);
      zone.classList.toggle('is-urgent', left < 24 * HEURE);
      return true;
    };

    if (!tick()) return;

    const timer = setInterval(() => {
      if (!tick()) clearInterval(timer);
    }, 30000);
  }

  /* ------------------------------------------------------- récapitulatif final */

  function renderRecap(root, recap) {
    const saved = store.read();

    // Le RIB, l'échéance et le bouton d'enregistrement sont rendus par Liquid :
    // ils doivent marcher même quand sessionStorage ne rend rien (autre
    // navigateur, onglet rouvert, navigation privée).
    renderDeadline(root, recap, saved || {});

    const printButton = recap.querySelector('[data-print]');
    if (printButton) {
      printButton.addEventListener('click', () => window.print());
    }

    if (!saved) {
      wireCopyButtons(recap);
      return;
    }

    const setText = (selector, value) => {
      if (!value) return;
      recap.querySelectorAll(selector).forEach((node) => {
        node.textContent = value;
      });
    };

    setText('[data-recap-reference]', saved.reference);
    setText('[data-recap-slot]', slotText(saved.slot));
    setText('[data-recap-total]', euro.format(saved.total));

    if (saved.name) {
      const meta = recap.querySelector('[data-recap-meta]');
      if (meta) {
        const group = document.createElement('div');
        const term = document.createElement('dt');
        term.textContent = 'Participant';
        const value = document.createElement('dd');
        value.textContent = saved.name;
        group.append(term, value);
        meta.prepend(group);
      }
    }

    const list = recap.querySelector('[data-recap-list]');
    if (list && saved.items && saved.items.length) {
      list.textContent = '';

      saved.items.forEach((item) => {
        const row = document.createElement('li');

        const category = document.createElement('span');
        category.className = 'atelier-zebre__ticket-cat';
        category.textContent = item.category;

        const value = document.createElement('span');
        value.className = 'atelier-zebre__ticket-value';
        value.textContent = item.title;

        if (item.ref) {
          const ref = document.createElement('em');
          ref.textContent = ` — réf. ${item.ref}`;
          value.append(ref);
        }

        const price = document.createElement('span');
        price.textContent = item.price > 0 ? `+ ${euro.format(item.price)}` : 'Inclus';

        row.append(category, value, price);
        list.append(row);
      });
    }

    wireCopyButtons(recap);
  }

  /* --- échéance du virement --- */

  function renderDeadline(root, recap, saved) {
    const zone = recap.querySelector('[data-deadline]');
    if (!zone) return;

    const panel = recap.querySelector('[data-pay]');
    const banner = recap.querySelector('.atelier-zebre__recap-banner');
    const countdown = zone.querySelector('[data-deadline-countdown]');
    const label = zone.querySelector('[data-deadline-label]');
    const dateNode = zone.querySelector('[data-deadline-date]');
    const expired = recap.querySelector('[data-deadline-expired]');

    const hours = Number(root.dataset.deadlineHours || 48) || 48;
    const deadline = deadlineFrom(saved.submittedAt, hours);

    // Page rouverte dans un autre navigateur : l'heure d'envoi est perdue.
    // On énonce le délai sans prétendre connaître la date limite.
    if (!deadline) {
      if (label) label.textContent = 'Tu as';
      if (countdown) countdown.textContent = `${hours} h`;
      if (dateNode) {
        dateNode.textContent = 'à compter de ta réservation pour faire le virement.';
        dateNode.hidden = false;
      }
      return;
    }

    const tick = () => {
      const left = remainingText(deadline, Date.now());

      if (left) {
        if (label) label.textContent = 'Il te reste';
        if (countdown) countdown.textContent = left;
        if (dateNode) {
          dateNode.textContent = `Soit avant le ${echeance.format(deadline)}.`;
          dateNode.hidden = false;
        }
        return true;
      }

      if (label) label.textContent = 'Délai dépassé';
      if (countdown) countdown.textContent = `${hours} h`;
      if (dateNode) {
        dateNode.textContent = `C'était avant le ${echeance.format(deadline)}.`;
        dateNode.hidden = false;
      }
      if (panel) panel.classList.add('is-expired');
      if (expired) expired.hidden = false;

      // « Ta place est retenue » devient faux une fois le délai passé : on retire
      // le bandeau plutôt que de laisser deux messages se contredire.
      if (banner) banner.hidden = true;
      return false;
    };

    if (!tick()) return;

    // La minute suffit : pas de rafraîchissement à la seconde pour un délai de
    // deux jours, et l'intervalle s'arrête de lui-même à l'échéance.
    const timer = setInterval(() => {
      if (!tick()) clearInterval(timer);
    }, 30000);
  }

  /* --- copie du RIB et de la référence --- */

  function wireCopyButtons(recap) {
    recap.querySelectorAll('[data-copy]').forEach((button) => {
      const row = button.closest('.atelier-zebre__pay-row');
      const source = row && row.querySelector('[data-copy-value]');
      if (!source) return;

      // Sans configuration retrouvée, la référence reste un tiret : mieux vaut
      // retirer le bouton que d'en laisser un qui ne fait rien.
      if (!source.textContent.trim() || source.textContent.trim() === '—') {
        button.hidden = true;
        return;
      }

      const labelNode = button.querySelector('[data-copy-label]');
      const initial = labelNode ? labelNode.textContent : '';
      let reset = null;

      button.addEventListener('click', async () => {
        // Un IBAN se saisit sans espaces dans une appli bancaire, mais se lit
        // mieux avec : on copie la version compacte, on affiche l'espacée.
        const raw = source.textContent.trim();
        const value = source.hasAttribute('data-copy-compact') ? raw.replace(/\s+/g, '') : raw;
        if (!value || value === '—') return;

        let done = false;

        try {
          await navigator.clipboard.writeText(value);
          done = true;
        } catch (error) {
          // Presse-papiers refusé (navigation privée, permission) : on
          // sélectionne la valeur pour que la copie manuelle soit immédiate.
          const range = document.createRange();
          range.selectNodeContents(source);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
        }

        if (labelNode) labelNode.textContent = done ? 'Copié' : 'À copier';
        button.classList.toggle('is-copied', done);

        clearTimeout(reset);
        reset = setTimeout(() => {
          if (labelNode) labelNode.textContent = initial;
          button.classList.remove('is-copied');
        }, 2000);
      });
    });
  }

  /* ------------------------------------------------------------ parcours actif */

  function enhance(root) {
    const form = root.querySelector('form');
    if (!form) return;

    const steps = Array.from(root.querySelectorAll('[data-step]'));
    if (!steps.length) return;

    const stepperItems = Array.from(root.querySelectorAll('[data-stepper-item]'));
    const mobileBar = root.querySelector('[data-mobile-bar]');
    const summaryList = root.querySelector('[data-summary-list]');
    const totalNodes = Array.from(root.querySelectorAll('[data-summary-total]'));

    let reference = makeReference();
    let currentStep = 1;

    root.classList.add('is-enhanced');

    renderClosing(root);

    // Les blocs imbriqués n'ont pas accès à l'id de leur catégorie parente :
    // c'est ici qu'on regroupe les radios pour obtenir un choix unique par catégorie.
    root.querySelectorAll('[data-category]').forEach((category) => {
      const name = `atelier-cat-${category.dataset.catId}`;
      category.querySelectorAll('[data-option]').forEach((input) => {
        input.name = name;
      });
    });

    /* --- compatibilité modèle → options --- */

    const modelCategory = root.querySelector('[data-model-category]');
    const modelHint = root.querySelector('[data-model-hint]');
    const otherCategories = Array.from(root.querySelectorAll('[data-category]')).filter(
      (category) => category !== modelCategory
    );

    function modelKeyOf(input) {
      return slug(input.dataset.modelKey || input.dataset.title);
    }

    function selectedModelKey() {
      if (!modelCategory) return null;
      const chosen = modelCategory.querySelector('[data-option]:checked');
      return chosen ? modelKeyOf(chosen) : null;
    }

    // Toutes les clés actuellement cochées, toutes catégories confondues : c'est
    // ce jeu que les listes d'autorisation et d'interdiction viennent interroger.
    function selectedKeys(exclude) {
      const keys = new Set();
      root.querySelectorAll('[data-option]:checked').forEach((input) => {
        if (exclude && exclude.contains(input)) return;
        optionKeys(input).forEach((key) => keys.add(key));
      });
      return keys;
    }

    const hits = (list, keys) => list.some((key) => keys.has(key));

    // Autorisé si la liste blanche est vide ou satisfaite, ET si aucune interdiction
    // n'est déclenchée. L'interdiction prime : c'est la forme qu'ont tes règles.
    function allowed(node, keys) {
      const allow = parseKeys(node.dataset.compatible);
      const block = parseKeys(node.dataset.incompatible);
      if (block.length && hits(block, keys)) return false;
      return !allow.length || hits(allow, keys);
    }

    // Un cadran, un bracelet, ou une catégorie entière, peut n'exister que sur
    // certains modèles : tout ce qui ne colle pas au modèle choisi disparaît, et
    // une option devenue incompatible est décochée plutôt que laissée en place.
    /* --- règles écrites à la main dans l'éditeur --- */

    // « Skeleton → masquer Date », « Boitier Or + Lunette Céramique → masquer Bracelet Argent ».
    // + combine (et), , sépare (ou). Le slug ramène « Aiguilles: Argent » sur la clé préfixée,
    // ce qui permet de viser une option précise quand son nom existe ailleurs.
    function parseRules(texte) {
      return String(texte || '')
        .split(/[\n;]+/)
        .map((ligne) => ligne.trim())
        .filter((ligne) => ligne && !ligne.startsWith('#'))
        .map((ligne) => {
          const parts = ligne.split(/→|->/);
          if (parts.length < 2) return null;

          const groupes = parts[0]
            .split(',')
            .map((groupe) => groupe.split('+').map(slug).filter(Boolean))
            .filter((groupe) => groupe.length);

          const droite = parts[1].trim();
          const verbe = /^\s*(masquer|cacher)\b/i.test(droite) ? 'masquer' : 'garder';
          const cibles = droite
            .replace(/^\s*(masquer|cacher|garder|afficher)\b/i, '')
            .split(',')
            .map(slug)
            .filter(Boolean);

          return groupes.length && cibles.length ? { groupes, verbe, cibles } : null;
        })
        .filter(Boolean);
    }

    const rules = parseRules(root.dataset.rules);

    // Une cible peut désigner une catégorie ou des options. On indexe les deux
    // séparément et la catégorie l'emporte : « masquer Bracelet Argent » vise la
    // catégorie, pas l'option « Argent » d'une catégorie « Bracelet ». Pour viser
    // une option précise, on l'écrit préfixée : « Bracelet: Argent ».
    const indexCategories = new Map();
    const indexOptions = new Map();
    const indexer = (map, key, node) => {
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(node);
    };
    root.querySelectorAll('[data-category]').forEach((category) => {
      indexer(indexCategories, slug(category.dataset.label), category);
      category.querySelectorAll('[data-option]').forEach((input) => {
        const label = input.closest('.atelier-zebre-option');
        if (label) optionKeys(input).forEach((key) => indexer(indexOptions, key, label));
      });
    });

    const resoudreCible = (key) => indexCategories.get(key) || indexOptions.get(key) || [];

    const regleActive = (regle, keys) =>
      regle.groupes.some((groupe) => groupe.every((terme) => keys.has(terme)));

    // Un groupe piloté par un autre ne doit pas s'afficher avant lui : sinon les
    // quatre familles de cadrans apparaissent d'un coup. On déduit des règles qui
    // pilote quoi, pour ne révéler chaque étape qu'une fois la précédente faite.
    const pilotes = new Map();
    rules.forEach((regle) => {
      const sources = new Set();
      regle.groupes.forEach((groupe) =>
        groupe.forEach((terme) => {
          (indexOptions.get(terme) || []).forEach((label) => {
            const parent = label.closest('[data-category]');
            if (parent) sources.add(parent);
          });
        })
      );
      if (!sources.size) return;

      regle.cibles.forEach((cible) => {
        resoudreCible(cible).forEach((node) => {
          const cat = node.matches('[data-category]') ? node : node.closest('[data-category]');
          if (!cat || cat === modelCategory) return;
          if (!pilotes.has(cat)) pilotes.set(cat, new Set());
          sources.forEach((s) => {
            if (s !== cat) pilotes.get(cat).add(s);
          });
        });
      });
    });

    function applyCompatibility() {
      if (!modelCategory) return;

      const key = selectedModelKey();
      const toutes = selectedKeys();

      // 1. visibilité de base, depuis les listes portées par chaque bloc
      otherCategories.forEach((category) => {
        // Les choix de la catégorie elle-même ne doivent pas conditionner
        // sa propre visibilité, sinon un choix se verrouillerait tout seul.
        const keys = selectedKeys(category);
        category.hidden = !(Boolean(key) && allowed(category, keys));

        category.querySelectorAll('[data-option]').forEach((input) => {
          const label = input.closest('.atelier-zebre-option');
          if (label) label.hidden = !allowed(input, keys);
        });
      });

      // 2. les règles écrites à la main, par-dessus
      rules.forEach((regle) => {
        const active = regleActive(regle, toutes);
        if (regle.verbe === 'masquer' ? !active : active) return;

        regle.cibles.forEach((cible) => {
          resoudreCible(cible).forEach((node) => {
            if (node === modelCategory || modelCategory.contains(node)) return;
            node.hidden = true;
          });
        });
      });

      // 3. révélation progressive : une catégorie attend que ses pilotes encore
      //    visibles aient été renseignés. On itère, un pilote pouvant lui-même
      //    dépendre d'un autre (type de boîtier → boîtier → bracelet).
      for (let passe = 0; passe < 6; passe += 1) {
        let bouge = false;

        pilotes.forEach((sources, cat) => {
          if (cat.hidden) return;

          // Un pilote ne compte que s'il concerne le modèle choisi : « Boitier »
          // est partagé SharpJust/SharpTona, et son pilote « Type de Boitier »
          // n'existe que sur SharpTona — sur SharpJust il ne doit rien retenir.
          const concernes = Array.from(sources).filter((source) => allowed(source, toutes));
          if (!concernes.length) return;

          // Parmi eux, seuls les visibles doivent être renseignés : sur SharpJust,
          // « Date » dépend de trois familles de cadrans dont une seule est active.
          // Si aucun n'est encore visible, la cible attend son tour.
          const visibles = concernes.filter((source) => !source.hidden);
          const enAttente =
            visibles.length === 0 ||
            visibles.some((source) => !source.querySelector('[data-option]:checked'));

          if (enAttente) {
            cat.hidden = true;
            bouge = true;
          }
        });

        if (!bouge) break;
      }

      // 4. on décoche ce qui a disparu, et on referme les catégories devenues vides
      otherCategories.forEach((category) => {
        let visibles = 0;
        category.querySelectorAll('[data-option]').forEach((input) => {
          const label = input.closest('.atelier-zebre-option');
          const visible = !category.hidden && label && !label.hidden;
          if (!visible && input.checked) input.checked = false;
          if (visible && !input.disabled) visibles += 1;
        });

        if (visibles === 0) category.hidden = true;
        if (category.hidden) {
          const error = category.querySelector('[data-category-error]');
          if (error) error.hidden = true;
        }
      });

      if (modelHint) modelHint.hidden = Boolean(key);
    }

    // Décocher une option peut en libérer ou en interdire d'autres : on repasse
    // jusqu'à ce que plus rien ne bouge (borné, pour ne jamais boucler).
    function settleCompatibility() {
      for (let pass = 0; pass < 5; pass += 1) {
        const before = root.querySelectorAll('[data-option]:checked').length;
        applyCompatibility();
        if (root.querySelectorAll('[data-option]:checked').length === before) return;
      }
    }

    // Une clé mal orthographiée ferait disparaître une option sans rien dire :
    // dans l'éditeur de thème, on la signale.
    function reportUnknownKeys() {
      const warning = root.querySelector('[data-compat-warning]');
      if (!warning || !modelCategory) return;

      const known = new Set();
      root.querySelectorAll('[data-option]').forEach((input) => {
        optionKeys(input).forEach((key) => known.add(key));
      });

      const declared = new Set();
      root.querySelectorAll('[data-category], [data-option]').forEach((node) => {
        parseKeys(node.dataset.compatible).forEach((value) => declared.add(value));
        parseKeys(node.dataset.incompatible).forEach((value) => declared.add(value));
      });

      // les règles écrites à la main visent aussi des catégories
      indexCategories.forEach((_, key) => known.add(key));
      rules.forEach((regle) => {
        regle.groupes.forEach((groupe) => groupe.forEach((t) => declared.add(t)));
        regle.cibles.forEach((c) => declared.add(c));
      });

      const unknown = Array.from(declared).filter((value) => !known.has(value));
      warning.hidden = unknown.length === 0;

      if (unknown.length) {
        warning.textContent =
          `Ces noms ne correspondent à rien : ${unknown.join(', ')}. ` +
          `Déplie « Noms utilisables » pour les noms exacts.`;
      }
    }

    // Sans la liste des noms, aucune règle n'est écrivable de tête.
    function buildCheatsheet() {
      const corps = root.querySelector('[data-cheatsheet-body]');
      if (!corps) return;

      const dl = document.createElement('dl');
      root.querySelectorAll('[data-category]').forEach((category) => {
        const dt = document.createElement('dt');
        dt.textContent = category.dataset.label;

        const dd = document.createElement('dd');
        const noms = Array.from(category.querySelectorAll('[data-option]')).map(
          (input) => input.dataset.title
        );
        dd.textContent = noms.join(' · ');

        dl.append(dt, dd);
      });

      corps.textContent = '';
      corps.append(dl);
    }

    /* --- affichage --- */

    function hideErrors() {
      root.querySelectorAll('[data-step-error], [data-category-error]').forEach((node) => {
        node.hidden = true;
      });
    }

    function showStep(step) {
      currentStep = step;
      hideErrors();

      steps.forEach((node) => {
        node.classList.toggle('is-active', Number(node.dataset.step) === step);
      });

      stepperItems.forEach((node) => {
        const index = Number(node.dataset.stepperItem);
        node.classList.toggle('is-current', index === step);
        node.classList.toggle('is-done', index < step);
      });

      if (mobileBar) mobileBar.hidden = step !== 2;

      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function renderSummary() {
      const data = collect(root);

      totalNodes.forEach((node) => {
        node.textContent = euro.format(data.total);
      });

      if (!summaryList) return;
      summaryList.textContent = '';

      if (!data.items.length) {
        const empty = document.createElement('li');
        empty.className = 'atelier-zebre__summary-empty';
        empty.textContent = 'Aucun composant choisi pour le moment.';
        summaryList.append(empty);
        return;
      }

      data.items.forEach((item) => {
        const row = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = item.category;
        const value = document.createElement('span');
        value.textContent = item.title;
        row.append(label, value);
        summaryList.append(row);
      });
    }

    /* --- validation --- */

    function showStepError(step) {
      const node = root.querySelector(`[data-step-error="${step}"]`);
      if (node) node.hidden = false;
    }

    function validate(step) {
      hideErrors();

      if (step === 1) {
        if (root.querySelector('[data-slot]:checked')) return true;
        showStepError(1);
        return false;
      }

      if (step === 2) {
        let firstMissing = null;

        root.querySelectorAll('[data-category]').forEach((category) => {
          if (category.hidden) return;
          if (category.dataset.required !== 'true') return;
          if (category.querySelector('[data-option]:checked')) return;

          const error = category.querySelector('[data-category-error]');
          if (error) error.hidden = false;
          if (!firstMissing) firstMissing = category;
        });

        if (!firstMissing) return true;

        showStepError(2);
        firstMissing.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }

      return true;
    }

    /* --- interactions --- */

    root.querySelectorAll('[data-next]').forEach((button) => {
      button.addEventListener('click', () => {
        const target = Number(button.dataset.next);
        if (!validate(target - 1)) return;
        showStep(target);
      });
    });

    root.querySelectorAll('[data-prev]').forEach((button) => {
      button.addEventListener('click', () => showStep(Number(button.dataset.prev)));
    });

    form.addEventListener('change', (event) => {
      if (event.target.matches('[data-option], [data-slot]')) {
        hideErrors();
        settleCompatibility();
        renderSummary();
      }
    });

    // Entrée sur une carte créneau ou une option ne doit pas envoyer le formulaire.
    form.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && currentStep !== LAST_STEP) {
        event.preventDefault();
      }
    });

    form.addEventListener('submit', (event) => {
      if (currentStep !== LAST_STEP) {
        event.preventDefault();
        return;
      }

      for (let step = 1; step < LAST_STEP; step += 1) {
        if (!validate(step)) {
          event.preventDefault();
          showStep(step);
          validate(step);
          return;
        }
      }

      const data = collect(root);

      // C'est cet instant qui fait courir le délai de virement, côté mail comme
      // côté récapitulatif : on le fige une fois, ici.
      const submittedAt = Date.now();
      const deadlineHours = Number(root.dataset.deadlineHours || 48) || 48;
      const deadline = deadlineFrom(submittedAt, deadlineHours);

      const fields = {
        reference,
        eventLabel: root.dataset.eventLabel || '',
        name: (form.querySelector('[name="contact[name]"]') || {}).value || '',
        phone: (form.querySelector('[name="contact[phone]"]') || {}).value || '',
        note: (form.querySelector('[data-note]') || {}).value || '',
        deadline: deadline ? echeance.format(deadline) : '',
      };

      const setField = (key, value) => {
        const node = root.querySelector(`[data-field="${key}"]`);
        if (node) node.value = value;
      };

      setField('reference', reference);
      setField('slot', slotText(data.slot));
      setField('components', componentsText(data.items));
      setField('total', euro.format(data.total));
      setField('deadline', fields.deadline);
      setField('body', bodyText(data, fields));

      store.save({
        reference,
        submittedAt,
        name: fields.name,
        phone: fields.phone,
        note: fields.note,
        slot: data.slot,
        items: data.items,
        basePrice: data.basePrice,
        total: data.total,
      });

      // Les radios de configuration n'ont rien à faire dans le POST /contact.
      root.querySelectorAll('[data-option], [data-slot]').forEach((input) => {
        input.disabled = true;
      });
    });

    /* --- reprise après un refus du formulaire par Shopify --- */

    // Un envoi rejeté (email mal formé…) recharge la page : sans ça, le participant
    // devrait refaire toute sa configuration pour corriger un seul champ.
    function restoreAfterError() {
      if (!root.querySelector('[data-form-errors]')) return false;

      const saved = store.read();
      if (!saved) return false;

      reference = saved.reference || reference;

      if (saved.slot) {
        const slotInput = Array.from(root.querySelectorAll('[data-slot]')).find(
          (input) => input.dataset.slotLabel === saved.slot.label && !input.disabled
        );
        if (slotInput) slotInput.checked = true;
      }

      (saved.items || []).forEach((item) => {
        const category = Array.from(root.querySelectorAll('[data-category]')).find(
          (node) => node.dataset.label === item.category
        );
        if (!category) return;

        const option = Array.from(category.querySelectorAll('[data-option]')).find(
          (input) =>
            input.dataset.title === item.title && input.dataset.ref === item.ref && !input.disabled
        );
        if (option) option.checked = true;
      });

      const phone = form.querySelector('[name="contact[phone]"]');
      if (phone && saved.phone) phone.value = saved.phone;

      const note = form.querySelector('[data-note]');
      if (note && saved.note) note.value = saved.note;

      return true;
    }

    const restored = restoreAfterError();

    settleCompatibility();
    reportUnknownKeys();
    buildCheatsheet();
    renderSummary();
    showStep(restored ? LAST_STEP : 1);
  }

  /* ------------------------------------------------------------------ amorçage */

  function init() {
    document.querySelectorAll('[data-atelier-zebre]').forEach((root) => {
      if (root.dataset.atelierZebreReady === 'true') return;
      root.dataset.atelierZebreReady = 'true';

      const recap = root.querySelector('[data-recap]');
      if (recap) {
        renderRecap(root, recap);
        return;
      }

      enhance(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Rechargement d'une section depuis l'éditeur de thème.
  document.addEventListener('shopify:section:load', init);
})();
